// @ts-ignore
/* eslint-disable */
// File định nghĩa các hàm gọi API dùng chung cho Ant Design Pro (user, login, notice, rule).
// Sử dụng `request` của @umijs/max — wrapper trên fetch, tự inject baseURL/headers/interceptor theo cấu hình UMI.
import { request } from "@umijs/max";

/**
 * Lấy thông tin user hiện tại đang đăng nhập.
 * Endpoint: POST {UMI_API_BASE_URL}/{UMI_API_OPTION_URL}/v1/user/get_user_info
 * - Trả về object `{ data: API.CurrentUser }` chứa thông tin profile.
 * - URL được build động từ biến môi trường UMI_API_BASE_URL & UMI_API_OPTION_URL.
 * - `options` cho phép caller override (vd: signal để abort, headers tuỳ chỉnh...).
 */
export async function currentUser(options?: Record<string, unknown>) {
  // return [{ id: 1, name: "John Doe" }];  // mock data dùng khi dev offline
  return request<{
    data: API.CurrentUser;
  }>(
    `${process.env.UMI_API_BASE_URL}/${process.env.UMI_API_OPTION_URL}/v1/user/get_user_info`,
    {
      method: "POST",
      ...(options || {}),
    }
  );
}

/**
 * Đăng xuất khỏi hệ thống.
 * Endpoint: POST /api/login/outLogin
 * - Gọi xong FE thường clear localStorage/token và redirect về trang login.
 */
export async function outLogin(options?: Record<string, unknown>) {
  return request<Record<string, unknown>>("/api/login/outLogin", {
    method: "POST",
    ...(options || {}),
  });
}

/**
 * Đăng nhập bằng tài khoản/mật khẩu.
 * Endpoint: POST {UMI_API_BASE_URL}/{UMI_API_OPTION_URL}/v1/user/get_user_info
 * - `body` (API.LoginParams): payload chứa username/password (và các field auth khác).
 * - Trả về API.LoginResult: token, status, currentAuthority...
 * - Lưu ý: hiện tại cùng endpoint với currentUser() — backend phân biệt qua payload.
 */
export async function login(
  body: API.LoginParams,
  options?: Record<string, unknown>
) {
  return request<API.LoginResult>(
    `${process.env.UMI_API_BASE_URL}/${process.env.UMI_API_OPTION_URL}/v1/user/get_user_info`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      data: body,
      ...(options || {}),
    }
  );
}

/**
 * Lấy danh sách thông báo (notice) cho icon chuông trên header.
 * Endpoint: GET /api/notices
 * - Trả về API.NoticeIconList — list notice phân loại theo type (notification/message/event).
 */
export async function getNotices(options?: Record<string, unknown>) {
  return request<API.NoticeIconList>("/api/notices", {
    method: "GET",
    ...(options || {}),
  });
}

/**
 * Lấy danh sách rule (có phân trang).
 * Endpoint: GET /api/rule
 * - `current`: trang hiện tại (1-based).
 * - `pageSize`: số bản ghi mỗi trang.
 * - Trả về API.RuleList: { data: RuleListItem[], total, success }.
 */
export async function rule(
  params: {
    // query
    /** Số trang hiện tại */
    current?: number;
    /** Số bản ghi mỗi trang */
    pageSize?: number;
  },
  options?: Record<string, unknown>
) {
  return request<API.RuleList>("/api/rule", {
    method: "GET",
    params: {
      ...params,
    },
    ...(options || {}),
  });
}

/**
 * Cập nhật một rule đã tồn tại.
 * Endpoint: POST /api/rule (body.method = "update")
 * - Convention nội bộ: dùng chung POST /api/rule cho CRUD, phân biệt qua field `method`.
 */
export async function updateRule(options?: Record<string, unknown>) {
  return request<API.RuleListItem>("/api/rule", {
    method: "POST",
    data: {
      method: "update",
      ...(options || {}),
    },
  });
}

/**
 * Tạo mới một rule.
 * Endpoint: POST /api/rule (body.method = "post")
 */
export async function addRule(options?: Record<string, unknown>) {
  return request<API.RuleListItem>("/api/rule", {
    method: "POST",
    data: {
      method: "post",
      ...(options || {}),
    },
  });
}

/**
 * Xoá rule.
 * Endpoint: POST /api/rule (body.method = "delete")
 * - Dù tên là removeRule và doc gốc ghi DELETE, code thực tế gửi POST với method="delete" trong body.
 */
export async function removeRule(options?: Record<string, unknown>) {
  return request<Record<string, unknown>>("/api/rule", {
    method: "POST",
    data: {
      method: "delete",
      ...(options || {}),
    },
  });
}
